import React, { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import PaymentStatus from '../../container/pages/paymantStatus';
import { useSelector } from 'react-redux';

const SourcePage = lazy(() => import('../../container/pages/SourcePage'));
const SalesAgentPage = lazy(() => import('../../container/pages/SalesAgentPage'));
const UserPage = lazy(() => import('../../container/pages/UserPage'));
const PackagePage = lazy(() => import('../../container/pages/PackagePage'));
const CreatePackagePage = lazy(() => import('../../container/pages/CreatePackagePage'));
const ViewPackagePage = lazy(() => import('../../container/pages/ViewPackagePage'));
const ViewSalesAgentPage = lazy(() => import('../../container/pages/ViewSalesAgentPage'));
const ViewUserPage = lazy(() => import('../../container/pages/ViewUserPage'));
const ProfilePage = lazy(() => import('../../container/pages/ProfilePage'));
const RequestSessionPage = lazy(() => import('../../container/pages/RequestSessionPage'));
const CommissionCalculatorPage = lazy(() => import('../../container/pages/CommissionCalculatorPage'));
const PaymentPage = lazy(() => import('../../container/pages/PaymentHistoryPage'));
const PaymentVerifyfulPage = lazy(() => import('../../container/pages/PaymentVerifyPage'));
const InvalidSlugPage = lazy(() => import('../../container/pages/InvalidSlugPage'));
const VerifyStripeOnboardingStatus = lazy(() => import('../../container/pages/VerifyStripeOnboardingStatusPage'));
const MerchantListPage = lazy(() => import('../../container/pages/MerchantListPage'));
const ViewMerchantListPage = lazy(() => import('../../container/pages/ViewMerchantListPage'));
const ReferralsListPage = lazy(() => import('../../container/pages/ReferralsListPage'));
const ViewReferralPage = lazy(() => import('../../container/pages/ViewReferralPage'));
const StaffsListPage = lazy(() => import('../../container/pages/StaffsListPage'));
const StaffProfilePage = lazy(() => import('../../container/pages/StaffProfilePage'));
const SalesAgentProfilePage = lazy(() => import('../../container/pages/SalesAgentProfilePage'));
const SalesAgentMerchantsPage = lazy(() => import('../../container/pages/SalesAgentMerchantsPage'));
const ViewSalesAgentMerchantPage = lazy(() => import('../../container/pages/ViewSalesAgentMerchantPage'));

function normalizePermissionLabelSlugs(permissionLabels) {
  if (!Array.isArray(permissionLabels)) return [];
  return permissionLabels
    .map((p) => {
      if (typeof p === 'string') return p;
      if (p && typeof p === 'object') return p.slug || p.label || '';
      return '';
    })
    .map((s) =>
      String(s || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-'),
    )
    .filter(Boolean);
}

function StaffPermissionRoute({ requiredSlugs = [], children }) {
  const role = useSelector((state) => state?.auth?.user?.role);
  const permissionLabels = useSelector((state) => state?.auth?.me?.permission_labels);
  if (role !== 'staff') return children;
  if (!Array.isArray(requiredSlugs) || requiredSlugs.length === 0) return children;

  const allowed = new Set(normalizePermissionLabelSlugs(permissionLabels));
  const hasPermission = requiredSlugs.some((slug) => allowed.has(String(slug || '').toLowerCase()));
  return hasPermission ? children : <Navigate to="/staff-profile" replace />;
}

/** Routes only for logged-in users with role `sales_agent`. */
function SalesAgentOnlyRoute({ children }) {
  const role = useSelector((state) => state?.auth?.user?.role);
  if (role === 'sales_agent') return children;
  if (role === 'staff') return <Navigate to="/staff-profile" replace />;
  if (role === 'merchant') return <Navigate to="/profile" replace />;
  return <Navigate to="/merchants" replace />;
}

function PagesRoute() {
  const UsersProfile = useSelector((state) => state?.auth?.me);
  const isCommissionCalculatorEnabled = UsersProfile?.package?.is_commission_calculator_enable;
  return (
    <Routes>
      <Route
        path="merchants/:id"
        element={
          <StaffPermissionRoute requiredSlugs={['merchant-management']}>
            <ViewUserPage />
          </StaffPermissionRoute>
        }
      />
      <Route
        path="merchants"
        element={
          <StaffPermissionRoute requiredSlugs={['merchant-management']}>
            <UserPage />
          </StaffPermissionRoute>
        }
      />
      <Route
        path="packages"
        element={
          <StaffPermissionRoute requiredSlugs={['package-management']}>
            <PackagePage />
          </StaffPermissionRoute>
        }
      />
      <Route
        path="packages/:id"
        element={
          <StaffPermissionRoute requiredSlugs={['package-management']}>
            <ViewPackagePage />
          </StaffPermissionRoute>
        }
      />
      <Route path="profile" element={<ProfilePage />} />
      <Route path="request-session" element={<RequestSessionPage />} />
      {isCommissionCalculatorEnabled && <Route path="commission-calculator" element={<CommissionCalculatorPage />} />}
      <Route path="payments" element={<PaymentPage />} />
      <Route path="invalid-slug" element={<InvalidSlugPage />} />
      <Route path="pay/verify-page" element={<PaymentVerifyfulPage />} />
      <Route path="/paymant-status" element={<PaymentStatus />} />
      <Route path="/create-package" element={<CreatePackagePage />} />
      <Route path="verify-stripe-onboarding-status" element={<VerifyStripeOnboardingStatus />} />
      <Route path="/sources" element={<SourcePage />} />
      <Route
        path="/sales-agents"
        element={
          <StaffPermissionRoute requiredSlugs={['sales-agent-management']}>
            <SalesAgentPage />
          </StaffPermissionRoute>
        }
      />
      <Route
        path="/sales-agents/:id"
        element={
          <StaffPermissionRoute requiredSlugs={['sales-agent-management']}>
            <ViewSalesAgentPage />
          </StaffPermissionRoute>
        }
      />
      <Route
        path="/merchant/requests"
        element={
          <StaffPermissionRoute requiredSlugs={['merchant-approval']}>
            <MerchantListPage />
          </StaffPermissionRoute>
        }
      />
      <Route
        path="/merchant/requests/:id"
        element={
          <StaffPermissionRoute requiredSlugs={['merchant-approval']}>
            <ViewMerchantListPage />
          </StaffPermissionRoute>
        }
      />
      <Route
        path="/referrals"
        element={
          <StaffPermissionRoute requiredSlugs={['referral-management']}>
            <ReferralsListPage />
          </StaffPermissionRoute>
        }
      />
      <Route
        path="/referrals/:id"
        element={
          <StaffPermissionRoute requiredSlugs={['referral-management']}>
            <ViewReferralPage />
          </StaffPermissionRoute>
        }
      />
      <Route path="/staffs" element={<StaffsListPage />} />
      <Route path="/staff-profile" element={<StaffProfilePage />} />
      <Route path="/sales-agent-profile" element={<SalesAgentProfilePage />} />
      <Route
        path="sales-agent-merchants"
        element={
          <SalesAgentOnlyRoute>
            <SalesAgentMerchantsPage />
          </SalesAgentOnlyRoute>
        }
      />
      <Route
        path="sales-agent-merchants/:id"
        element={
          <SalesAgentOnlyRoute>
            <ViewSalesAgentMerchantPage />
          </SalesAgentOnlyRoute>
        }
      />
    </Routes>
  );
}

export default PagesRoute;
